import Immutable, { type List, type Map } from 'immutable';
import { combineReducers } from 'redux-immutable';
import liveDashboard from '../containers/LiveDashboard/reducer';

import productReducer from '../containers/ProductSettings/reducer';
import adminReducer from '../containers/Admin/reducer';
import salesReducer from '../containers/Sales/reducer';
import {
  LOGOUT,
  GET_SHOP_LIST,
  SET_SHOP_NAME,
  GET_PRODUCT_LIST,
  SET_USER_ROLE,
  SWITCH_SHOP,
  GET_USER_PERMISSIONS,
} from '../actions/actions';
import globalSettingsReducer from '../reducers/globalsReducer';
import analysisReducer from '../containers/Analysis/reducer';
import activationDesignerReducer from '../containers/ActivationDesigner/reducer';
import mailTemplatesDesignerReducer from '../containers/MailTemplateDesigner/reducer';
import abtestDesignerReducer from '../containers/ABTestDesigner/reducer';
import fuzzyReducer from '../containers/FuzzyEditor/reducer';

import { Testable } from '../utils/testing-utils';
import shopDataReducer from './shopDataReducer';

import type { AccountShopsResponse } from 'api/models/shops/accountShops.response';
import type { ShopData } from 'shared/types';
import type { LegacyAction } from '../middleware/api-middleware.types';

interface DashboardActionData {
  [index: number]: AccountShopsResponse[number] | undefined;
  data?: unknown;
  length?: number;
}

interface DashboardAction extends LegacyAction {
  data?: DashboardActionData;
  name?: ShopData['name'];
  shop?: ShopData['id'];
  shopId?: ShopData['shopId'] | string;
  userRole?: number;
}

type DashboardState = Map<string, unknown>;
const initialState = Immutable.fromJS({
  shops: [],
  shopsLoading: false,
  products: [],
  role: null,
  shopsRecieved: false,
  selectedShop: undefined,
  selectedShopId: undefined,
  permissions: [],
});

const isShopList = (value: unknown): value is List<Map<string, unknown>> => {
  if (!Immutable.List.isList(value)) {
    return false;
  }

  const list = value as List<unknown>;
  return list.every((item?: unknown) => Immutable.Map.isMap(item));
};

function userDataReducer(
  state: DashboardState = initialState,
  action: DashboardAction,
): DashboardState {
  switch (action.type) {
    case GET_USER_PERMISSIONS: {
      return state.merge({
        permissions: action.data!.data,
      });
    }
    case `${GET_SHOP_LIST}_LOADING`: {
      return state.merge({
        shopsLoading: true,
      });
    }
    case GET_SHOP_LIST: {
      const newState = state.merge({
        shops: Immutable.fromJS(action.data!),
        shopsRecieved: true,
        shopsLoading: false,
      });

      return newState;
    }
    case `${SET_SHOP_NAME}_LOADING`: {
      return state.merge({
        shopsLoading: true,
      });
    }
    case SET_SHOP_NAME: {
      const currentShop = state.get('selectedShop');
      const shops = state.get('shops');
      if (!isShopList(shops)) {
        throw new TypeError('Expected dashboard shops to be an Immutable List');
      }

      const index = shops.findIndex(
        (item?: Map<string, unknown>) => item?.get('id') === currentShop,
      );

      const updatedShops = shops.update(
        index,
        (item?: Map<string, unknown>) => {
          if (!item) {
            throw new TypeError('Expected a shop entry at the selected index');
          }

          return item.set('name', action.name);
        },
      );

      const newState = state.merge({
        shops: updatedShops,
        shopsLoading: false,
      });

      return newState;
    }
    case GET_PRODUCT_LIST: {
      return state.set('products', Immutable.fromJS(action.data!.data));
    }
    case SET_USER_ROLE: {
      return state.set('role', action.userRole);
    }
    case SWITCH_SHOP: {
      return state.merge({
        selectedShop: action.shop,
        selectedShopId: action.shopId!.toString(),
      });
    }
    default: {
      return state;
    }
  }
}

const customerBackendDashboardReducer = combineReducers({
  userData: userDataReducer,
  global: globalSettingsReducer,
  liveDashboard,
  admin: adminReducer,
  sales: salesReducer,
  analysis: analysisReducer,
  shops: shopDataReducer,
  products: productReducer,
  activationDesigner: activationDesignerReducer,
  mailTemplates: mailTemplatesDesignerReducer,
  abTests: abtestDesignerReducer,
  fuzzyEditor: fuzzyReducer,
});

const customerBackendDashboardRootReducer = (
  state: DashboardState | undefined,
  action: DashboardAction,
): DashboardState => {
  let newState = state;
  if (action.type === LOGOUT) {
    newState = undefined;
  }

  return Reflect.apply(customerBackendDashboardReducer, undefined, [
    newState,
    action,
  ]);
};

export default Testable(customerBackendDashboardRootReducer, {
  userDataReducer,
});
